// Re-exported from the window-free ./origins module so the edge dd-logs proxy
// can import the pattern without pulling in this browser-only module.
import { VERCEL_PREVIEW_ORIGIN_RE } from './origins';

export { VERCEL_PREVIEW_ORIGIN_RE };

const PROD_ORIGIN = 'https://admin.oath.vote';
const STAGING_ORIGIN = 'https://test-admin.oath.vote';
// Anchored to the full hostname so look-alikes (e.g. oath.vote.local.copycat.com) don't match.
const LOCAL_HOSTNAME_RE = /^(oath\.vote\.local|localhost|127\.0\.0\.1)$/;
// Same-origin proxy to Datadog (see api/dd-logs.ts). Ad-block lists block Datadog by domain, so routing through our own origin circumvents ad-blocking.
const DD_LOGS_PROXY_URI = '/api/dd-logs';

const HELP_CENTER_URL = 'https://helpcenter.oath.vote/oath-help-center';
const RECIPIENT_HELP_CENTER_URL = 'https://helpcenter.oath.vote/recipient-help-center';
const RECIPIENTS_SUPPORT_EMAIL = 'recipients@oath.vote';

const DATADOG_LOGS_INIT_BASE = {
  clientToken: 'pube72af0ea4956fd4be4f67e2b55636c87',
  site: 'datadoghq.com',
  forwardErrorsToLogs: true,
  service: 'browser-oath-admin',
  sessionSampleRate: 100,
  proxy: DD_LOGS_PROXY_URI,
  beforeSend: log => {
    // Drop logs for 4xx error response from the Auth0 token endpoint (expired/invalid refresh token)
    if (log.http?.url?.includes('/oauth/token') && log.http.status_code < 500) {
      return false;
    }
    return true;
  },
};

// Single source of truth for which deployment environment we're in; every
// environment-specific value below is derived from it.
export type Environment = 'production' | 'staging' | 'preview' | 'local';

// Datadog's env tag per environment — an explicit map (not just the Environment
// value) because prod is abbreviated and existing DD dashboards/monitors filter
// on these exact strings. Record<Environment, …> forces every env to be covered.
const DD_ENV: Record<Environment, string> = {
  production: 'prod',
  staging: 'staging',
  preview: 'preview',
  local: 'local',
};

// Fields each config derives from its Environment: the tag itself + the DD logger env.
const envFields = (ENV: Environment) => ({
  ENV,
  DATADOG_LOGS_INIT: { ...DATADOG_LOGS_INIT_BASE, env: DD_ENV[ENV] },
});

// Locked staging backend — STAGING_CONFIG always hits this regardless of any other defined env config.
export const STAGING_API_BASE_URL = 'https://admin-api-staging-95t3.onrender.com';
export const PROD_API_BASE_URL = 'https://admin-api-prod-4er0.onrender.com';

// Local dev hits /backend (same origin); Caddy proxies it to BACKEND_UPSTREAM.
const LOCAL_API_BASE_URL =
  process.env.NODE_ENV === 'development' && process.env.REACT_APP_ENV === 'local'
    ? '/backend'
    : STAGING_API_BASE_URL;

const NON_PROD_BASE = {
  PARTNER_PAGE: 'https://test.oath.vote/donate?p=',
  EVENT_PAGE: 'https://test.oath.vote/donate?event&p=',
  COMMUNITY_PAGE: 'https://test.oath.vote/set?p=',
  SHOPPING_CART_PAGE: 'https://test.oath.vote/search?client_id=',
  CLOUD_STORAGE: {
    URL: 'https://storage.googleapis.com/oath_1',
    TAGS: 'test_tags',
    SETS: 'test_sets',
  },
  HELP_CENTER_URL: HELP_CENTER_URL,
  RECIPIENT_HELP_CENTER_URL: RECIPIENT_HELP_CENTER_URL,
  RECIPIENTS_SUPPORT_EMAIL: RECIPIENTS_SUPPORT_EMAIL,
};

const PROD_CONFIG = {
  // hardcoding for production because it never changes and it's more secure
  ...envFields('production'),
  SITE_URL: PROD_ORIGIN,
  PARTNER_PAGE: 'https://app.oath.vote/donate?p=',
  EVENT_PAGE: 'https://app.oath.vote/donate?event&p=',
  COMMUNITY_PAGE: 'https://app.oath.vote/set?p=',
  SHOPPING_CART_PAGE: 'https://app.oath.vote/search?client_id=',
  CLOUD_STORAGE: {
    URL: 'https://storage.googleapis.com/oath_1',
    SETS: 'sets',
    TAGS: 'tags',
  },
  API: {
    BASE_URL: PROD_API_BASE_URL,
  },
  HELP_CENTER_URL: HELP_CENTER_URL,
  RECIPIENT_HELP_CENTER_URL: RECIPIENT_HELP_CENTER_URL,
  RECIPIENTS_SUPPORT_EMAIL: RECIPIENTS_SUPPORT_EMAIL,
};

const STAGING_CONFIG = {
  ...NON_PROD_BASE,
  ...envFields('staging'),
  API: { BASE_URL: STAGING_API_BASE_URL },
};

const PREVIEW_CONFIG = {
  ...NON_PROD_BASE,
  ...envFields('preview'),
  API: { BASE_URL: STAGING_API_BASE_URL },
};

const LOCAL_CONFIG = {
  ...NON_PROD_BASE,
  ...envFields('local'),
  API: { BASE_URL: LOCAL_API_BASE_URL },
};

const selectConfig = (origin: string, hostname: string) => {
  if (origin === PROD_ORIGIN) return PROD_CONFIG;
  if (origin === STAGING_ORIGIN) return STAGING_CONFIG;
  if (VERCEL_PREVIEW_ORIGIN_RE.test(origin)) return PREVIEW_CONFIG;
  if (LOCAL_HOSTNAME_RE.test(hostname)) return LOCAL_CONFIG;
  // Unknown origins fall back to staging
  return STAGING_CONFIG;
};

// Matches a relative path or a local hostname anywhere in an API base URL.
const LOCAL_API_URL_RE = /^\/|localhost|127\.0\.0\.1|oath\.vote\.local/;

// Fires the local-bypass warning once per session, like the old module-load check did.
let warnedLocalBypass = false;

export const ENV_CONFIG = () => {
  const { origin, hostname } = window.location;
  const isLocalHost = LOCAL_HOSTNAME_RE.test(hostname);

  // Warn (instead of failing silently) when we're on a local host but the /backend
  // proxy is off — requests would go straight to staging, bypassing Caddy.
  // Skipped under test (jsdom runs on localhost, which would trip this on every suite).
  if (
    !warnedLocalBypass &&
    process.env.NODE_ENV !== 'test' &&
    isLocalHost &&
    LOCAL_API_BASE_URL !== '/backend'
  ) {
    warnedLocalBypass = true;
    console.warn(
      `[env] Local host but REACT_APP_ENV="${process.env.REACT_APP_ENV}" (expected "local"): ` +
        `bypassing the Caddy /backend proxy and calling ${STAGING_API_BASE_URL} directly. ` +
        `Start via "make up" with .env.local present.`
    );
  }

  // SITE_URL defaults to the live origin for non-prod; PROD_CONFIG pins it explicitly.
  const config = { SITE_URL: origin, ...selectConfig(origin, hostname) };
  // Defense-in-depth: a relative or local API URL must never be used off a local host.
  // Catches a future regression that points a cloud config at /backend or localhost,
  // forcing a safe staging fallback instead of a broken (or data-leaking) request.
  if (!isLocalHost && LOCAL_API_URL_RE.test(config.API.BASE_URL)) {
    console.error(
      `[env] Blocked local API URL "${config.API.BASE_URL}" on ${origin}; falling back to staging.`
    );
    return { ...config, API: { BASE_URL: STAGING_API_BASE_URL } };
  }
  return config;
};
